import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

const formatSearchDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

export default function DashboardSavedFiltersBar({
  savedSearches,
  defaultSearchId,
  activeSearchId,
  onApplySearch,
  onSaveSearch,
  onDeleteSearch,
  onRenameSearch,
  onSetDefault,
  onClearDefault,
  onResetFilters,
  isDarkMode,
}) {
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false);
  const [manageDialogOpen, setManageDialogOpen] = React.useState(false);
  const [saveName, setSaveName] = React.useState('');
  const [setAsDefault, setSetAsDefault] = React.useState(false);
  const [editingSearchId, setEditingSearchId] = React.useState(null);
  const [editingName, setEditingName] = React.useState('');

  const selectValue = activeSearchId || 'default';

  const openSaveDialog = () => {
    setSaveName('');
    setSetAsDefault(false);
    setSaveDialogOpen(true);
  };

  const handleSave = () => {
    const trimmedName = saveName.trim();
    if (!trimmedName) return;
    onSaveSearch({ name: trimmedName, setAsDefault });
    setSaveDialogOpen(false);
  };

  const startRename = (search) => {
    setEditingSearchId(search.id);
    setEditingName(search.name);
  };

  const commitRename = () => {
    const trimmedName = editingName.trim();
    if (!trimmedName || !editingSearchId) return;
    onRenameSearch(editingSearchId, trimmedName);
    setEditingSearchId(null);
    setEditingName('');
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="dashboard-saved-filter-label">Saved filter</InputLabel>
        <Select
          labelId="dashboard-saved-filter-label"
          value={selectValue}
          label="Saved filter"
          onChange={(event) => {
            const value = event.target.value;
            if (value === 'default') {
              onResetFilters();
              return;
            }
            onApplySearch(value);
          }}
          sx={{
            fontSize: '0.875rem',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            },
          }}
        >
          <MenuItem value="default">Default</MenuItem>
          {savedSearches.map((search) => (
            <MenuItem key={search.id} value={search.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {defaultSearchId === search.id ? (
                  <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                ) : null}
                <Box>
                  <Typography component="span" sx={{ fontSize: '0.875rem' }}>
                    {search.name}
                  </Typography>
                  {savedSearches.filter((item) => item.name === search.name).length > 1 && (
                    <Typography component="span" sx={{ ml: 0.75, fontSize: '0.75rem', color: 'text.secondary' }}>
                      ({formatSearchDate(search.createdAt)})
                    </Typography>
                  )}
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        size="small"
        variant="outlined"
        startIcon={<BookmarkBorderIcon fontSize="small" />}
        onClick={openSaveDialog}
        sx={{
          textTransform: 'none',
          fontSize: '0.8rem',
          borderColor: isDarkMode ? '#334155' : '#e0e0e0',
          color: 'text.primary',
        }}
      >
        Save
      </Button>

      <Tooltip title="Manage saved filters">
        <IconButton
          size="small"
          onClick={() => setManageDialogOpen(true)}
          aria-label="Manage saved filters"
          sx={{ color: 'text.secondary' }}
        >
          <SettingsOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Typography
        component="button"
        type="button"
        variant="body2"
        onClick={onResetFilters}
        sx={{
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          color: 'primary.main',
          fontSize: '0.8rem',
          textDecoration: 'underline',
          '&:hover': { opacity: 0.8 },
        }}
      >
        Reset filters
      </Typography>

      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Save filter</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Name"
            value={saveName}
            onChange={(event) => setSaveName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSave();
              }
            }}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={setAsDefault}
                onChange={(event) => setSetAsDefault(event.target.checked)}
              />
            )}
            label="Set as default for dashboard"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!saveName.trim()}
            sx={{
              backgroundColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : '#fff',
              '&:hover': {
                backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Saved dashboard filters
        </DialogTitle>
        <DialogContent>
          {savedSearches.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No saved filters yet.
            </Typography>
          ) : (
            <List dense>
              {savedSearches.map((search) => (
                <ListItem key={search.id} sx={{ px: 0 }}>
                  {editingSearchId === search.id ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editingName}
                      onChange={(event) => setEditingName(event.target.value)}
                      onBlur={commitRename}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          commitRename();
                        }
                      }}
                    />
                  ) : (
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {defaultSearchId === search.id ? (
                            <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          ) : (
                            <StarBorderIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          )}
                          <Typography component="span" sx={{ fontSize: '0.9rem' }}>
                            {search.name}
                          </Typography>
                        </Box>
                      )}
                      secondary={formatSearchDate(search.updatedAt || search.createdAt)}
                    />
                  )}
                  <ListItemSecondaryAction>
                    <Tooltip title="Rename">
                      <IconButton edge="end" size="small" onClick={() => startRename(search)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={defaultSearchId === search.id ? 'Clear default' : 'Set as default'}>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          if (defaultSearchId === search.id) {
                            onClearDefault();
                          } else {
                            onSetDefault(search.id);
                          }
                        }}
                      >
                        {defaultSearchId === search.id ? (
                          <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                        ) : (
                          <StarBorderIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => onDeleteSearch(search.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setManageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
